import { ShowAllData } from '@/apiError/apiShow'; // Assuming it's in your apiShow file
import { userData } from '@/common/custom-date-selector-list/helpers';
import { decryptData } from '@/components/reusable/crypto/Cipher';
import useAppData from '@/hooks/useAppData';
import useDeviceInfo from '@/theme/device-info';
import { formatNumberWithComma } from '@/utils/formula/CodeWindow';
import { showToast } from '@/utils/toastMessage/ToastMessage';
import { Table } from 'antd';
import useApp from 'antd/es/app/useApp';
import Decimal from 'decimal.js';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { roundTo3 } from '../purchase/helpers';
export const CodeGenerateAfterPartySelect = async (
  value,
  rowIndex,
  itemName,
  codeInputs,
  setCodeInputs,
  purchaseTableData,
  setSelectedData,
  setPurchaseTableData,
  setPrefixResponse,
) => {
  const prefixResponse = await ShowAllData?.showDetails(
    `/transaction/items/latest-code/?itemId=${value}`,
  );

  setPrefixResponse(prefixResponse?.data);

  const newPrefix = prefixResponse?.data?.itemBarcodePrefix;
  const apiCode = prefixResponse?.data?.itemCode;

  let codeToUse = apiCode;

  if (newPrefix) {
    const matchingRows = purchaseTableData?.filter(
      (row) => row.itemBarcodePrefix === newPrefix && row.code != null,
    );

    if (matchingRows?.length > 0) {
      const existingSuffixes = matchingRows
        .map((row) => parseInt(row.code?.replace(newPrefix, ''), 10))
        .filter((n) => !isNaN(n));

      if (existingSuffixes.length > 0) {
        const maxExisting = Math.max(...existingSuffixes);
        const apiSuffix = parseInt(apiCode?.replace(newPrefix, ''), 10);
        const base = isNaN(apiSuffix)
          ? maxExisting
          : Math.max(maxExisting, apiSuffix);
        codeToUse = `${String(base + 1).padStart(3, '0')}`;
      }
    }
  }
  setSelectedData((prevData) => ({
    ...prevData,
    item: value,
    itemName: [...(prevData.itemName || []), itemName],
    code: [...(prevData.code || []), codeToUse],
  }));

  setPurchaseTableData((prevData) =>
    prevData?.map((row, index) =>
      index === rowIndex
        ? {
            ...row,
            itemName,
            item: value,
            itemType: prefixResponse?.data?.itemType,
            code: codeToUse,
            itemBarcodePrefix: newPrefix,
          }
        : row,
    ),
  );
};

export const CodeGenerateAfterPartySelectStockAdj = async (
  value,
  rowIndex,
  itemName,
  codeInputs,
  setCodeInputs,
  purchaseTableData,
  setSelectedData,
  setPurchaseTableData,
  setPrefixResponse,
) => {
  const prefixResponse = await ShowAllData?.showDetails(
    `/transaction/items/latest-code/?itemType=${value}`,
  );

  // setPrefixResponse(prefixResponse?.data);
  const newCode = prefixResponse?.data?.itemCode; // Get the new code
  if (newCode === 'NA') {
    const updatedCodeInputs = [...codeInputs];

    updatedCodeInputs[rowIndex] = 'NA'; // Set "NA" directly

    setCodeInputs(updatedCodeInputs);

    setSelectedData((prevData) => ({
      ...prevData,
      item: value,
      code: [...(prevData.code || []), 'NA'],
      itemName: [...(prevData.itemName || []), itemName],
    }));

    setPurchaseTableData((prevData) =>
      prevData?.map((row, index) =>
        index === rowIndex
          ? { ...row, itemName, item: value, code: 'NA' }
          : row,
      ),
    );

    return; // Exit early since we don't need to generate a number
  }
  const baseCode = newCode?.replace(/\d+$/, ''); // Extract the base part of the code
  let codeSuffix = parseInt(newCode?.match(/\d+$/)[0], 10); // Extract the numeric suffix

  // Find all existing codes that match the base code
  const existingCodes = purchaseTableData
    .filter((row) => row.code && row.code.startsWith(baseCode))
    .map((row) => parseInt(row.code.replace(baseCode, ''), 10));

  // Determine the next available suffix
  if (existingCodes?.length > 0) {
    codeSuffix = Math?.max(...existingCodes) + 1;
  }

  const uniqueCode = `${baseCode}${String(codeSuffix).padStart(3, '0')}`; // Construct the unique code

  const updatedCodeInputs = [...codeInputs];
  updatedCodeInputs[rowIndex] = uniqueCode; // Update the specific row's code

  setCodeInputs(updatedCodeInputs);

  setSelectedData((prevData) => ({
    ...prevData,
    item: value,
    code: [...(prevData.code || []), uniqueCode],
    itemName: [...(prevData.itemName || []), itemName],
  }));

  setPurchaseTableData((prevData) =>
    prevData?.map((row, index) =>
      index === rowIndex
        ? { ...row, itemName, item: value, code: uniqueCode }
        : row,
    ),
  );
};

export const updateCodeWindowJson = (tableData) => {
  const excludeItemDetailsKeys = [
    'percent',
    'crt',
    'percent',
    'diamondWeightCrt',
    'diamondWeightGram',
    'dswt',
    'netWeight',
    'pureWeight',
    'stoneAmount',
    'stonePcs',
    'stoneWeightCrt',
    'stoneWeightGram',
    'new',
    'metalAmount',
    'netWithWastage',
    'itemType',
    'grossWeightGm',
  ];

  const excludeDiamondStoneKeys = ['itemTypesName', 'itemTypeName', 'itemName'];
  const defaultValuesKey = ['pcs', 'weight', 'purchaseRate', 'salesRate'];
  const sanitizeValue = (key, value) => {
    // if (key === 'itemDetailId' && !value) {
    //   return;
    // }
    if (value === '' || value === null || value === undefined) {
      // Return 0 unless the key is 'makingChargeType' or 'WastageType'
      if (
        key === 'itemDetailId' ||
        key === 'karigarId' ||
        key === 'size' ||
        key === 'orderDetailId' ||
        key === 'remarks'
      ) {
        return null;
      }
      if (key === 'code' || key === 'trayName') {
        return value;
      }
      if (key === 'taxableAmount' || key === 'vatableAmount') {
        return value;
      }
      if (
        key !== 'makingChargeType' &&
        key !== 'WastageType' &&
        key !== 'sMakingChargeType' &&
        key !== 'sWastageType' &&
        key !== 'description' &&
        key !== 'referenceItemCode'
      ) {
        return 0;
      }
    }
    return value;
  };

  return {
    itemDetails: tableData?.map((item) => {
      // Handle the diamondStoneJson filtering
      // console.log('tableData', item);
      const { finalData: testFinalData, ...otherData } = item;

      const diamondStoneJson = Array.isArray(item?.finalData?.diamondStoneJson)
        ? item.finalData.diamondStoneJson
        : Array.isArray(item?.diamondStones)
          ? item?.diamondStones
          : [];

      const diamondStoneJsonFiltered = diamondStoneJson?.filter(
        (item) => item?.itemId,
      );
      const filteredDiamondStoneJson = diamondStoneJsonFiltered?.map(
        (stone) => {
          const transformed = Object.fromEntries(
            Object.entries(stone)
              .filter(
                ([key]) => !excludeDiamondStoneKeys.includes(key), // Exclude unwanted keys
              )
              ?.map(([key, value]) => {
                if (defaultValuesKey.includes(key)) {
                  return [
                    key,
                    value === '' || value === null || value === undefined
                      ? 0
                      : value,
                  ];
                }
                return [key, value];
              }),
          );
          defaultValuesKey.forEach((key) => {
            if (!(key in transformed)) {
              transformed[key] = 0;
            }
          });
          return transformed;
        },
      );

      // Handle the filtering for detailsJson within finalData
      const detailsJson = item?.finalData?.detailsJson?.[0] || item || {};

      const flattenedDetailsJson = Object.fromEntries(
        Object.entries(detailsJson)
          .filter(([key]) => !excludeItemDetailsKeys.includes(key))
          .map(([key, value]) => [key, sanitizeValue(key, value)]), // Sanitize values
      );
      // const diamondStoneData
      const totalDiamondStoneAmount = (
        item?.finalData?.diamondStoneJson || item?.diamondStones
      )?.reduce((acc, item) => acc + (item?.amount || 0), 0);
      // Construct the combined item
      const combinedItem = {
        ...flattenedDetailsJson,

        // Use the first entry of detailsJson, if available
        codeWindowId: item?.id || item?.codeWindowId, // Ensure codeWindowId is included
        itemCode: item?.code || item?.itemCode || null,
        subTotal: item?.netAmount,
        // itemName: item?.itemName,
        purity: item?.finalData?.detailsJson[0]?.purity || item?.purity,
        taxableAmount: item?.taxableAmount || 0,
        vatableAmount: roundTo3(
          item?.vatableAmount || totalDiamondStoneAmount || 0,
        ),
        // itemId: item?.item,
        // ...otherData,
        diamondStoneJson: filteredDiamondStoneJson, // Use the filtered diamondStoneJson
        ...(item?.isUseStock && { isUseStock: item?.isUseStock }),
        ...(item?.calculateOnPure && {
          calculateOnPure: item?.calculateOnPure,
        }),
      };

      // Clean up unnecessary properties if needed
      delete combinedItem?.diamondStones;
      delete combinedItem?.summaryData;
      delete combinedItem?.finalData;

      // if (
      //   combinedItem.itemDetailId == null ||
      //   combinedItem.itemDetailId === undefined
      // ) {
      //   delete combinedItem.itemDetailId;
      // }

      return combinedItem;
    }),
  };
};

export const issueRefineOnly = (tableData) => {
  const excludeItemDetailsKeys = [
    'purity',
    'crt',
    'percent',
    'diamondWeightCrt',
    'diamondWeightGram',
    'dswt',
    'itemName',
    'netWeight',
    'pureWeight',
    'stoneAmount',
    'stonePcs',
    'stoneWeightCrt',
    'stoneWeightGram',
    'new',
    'metalAmount',
    'netWithWastage',
    'itemType',
  ];

  const excludeDiamondStoneKeys = ['itemTypesName', 'itemTypeName', 'itemName'];
  const sanitizeValue = (key, value) => {
    if (value === '' || value === null || value === undefined) {
      // Return 0 unless the key is 'makingChargeType' or 'WastageType'
      if (
        key !== 'makingChargeType' &&
        key !== 'WastageType' &&
        key !== 'sMakingChargeType' &&
        key !== 'sWastageType' &&
        key != 'description'
      ) {
        return 0;
      }
    }
    return value;
  };

  return {
    itemDetails: tableData?.map((item) => {
      // Handle the diamondStoneJson filtering
      const diamondStoneJson = Array.isArray(item?.finalData?.diamondStoneJson)
        ? item.finalData.diamondStoneJson
        : Array.isArray(item?.diamondStones)
          ? item?.diamondStones
          : [];

      const filteredDiamondStoneJson = diamondStoneJson?.map((stone) => {
        return Object.fromEntries(
          Object.entries(stone).filter(
            ([key]) => !excludeDiamondStoneKeys.includes(key), // Exclude unwanted keys
          ),
        );
      });

      // Handle the filtering for detailsJson within finalData
      const detailsJson = item?.finalData?.detailsJson?.[0] || item || {};
      const flattenedDetailsJson = Object.fromEntries(
        Object.entries(detailsJson)
          .filter(([key]) => !excludeItemDetailsKeys.includes(key))
          .map(([key, value]) => [key, sanitizeValue(key, value)]), // Sanitize values
      );

      // Construct the combined item
      const combinedItem = {
        ...flattenedDetailsJson, // Use the first entry of detailsJson, if available
        codeWindowId: item?.id || item?.codeWindowId, // Ensure codeWindowId is included
        itemCode: item?.code || item?.itemCode,
        purity: item?.finalData?.detailsJson[0]?.percent || item?.purity,
        itemId: flattenedDetailsJson?.itemId || item?.itemId,
        itemDetailsId: item?.itemDetailsId,
        diamondStoneJson: filteredDiamondStoneJson, // Use the filtered diamondStoneJson
      };

      // Clean up unnecessary properties if needed
      delete combinedItem?.diamondStones;
      delete combinedItem?.summaryData;
      delete combinedItem?.finalData;

      return combinedItem;
    }),
  };
};

export const handleVoucherFetchApiClicked = async ({
  ledgerUrl,
  voucherUrl,
  setBillNumber,
  selectedData = {},
  setCashBankLedgerData,
  setReceiveModalVisible,
}) => {
  const ledgerData = await ShowAllData.showDetails(ledgerUrl);
  const ledgerList =
    ledgerData?.data?.paidToLedger || ledgerData?.data?.receiveFromLedger;
  const isLedgerValid =
    ledgerList?.length &&
    ledgerList?.some((item) => item.referenceId === selectedData?.ledger);

  if (!isLedgerValid) {
    showToast('The party is not opened on Sundry Debtors.', 'warn');
    return;
  }
  const voucherNumber = await ShowAllData.showDetails(voucherUrl);
  setReceiveModalVisible(true);
  setBillNumber(voucherNumber?.data);
  setCashBankLedgerData(ledgerData?.data);
};

export const calculateTotals = (key, tableData) => {
  // Check if tableData is a valid array
  if (!Array.isArray(tableData) || tableData?.length === 0) {
    return 0; // Return 0 if the data is missing or empty
  }

  // Safely reduce the data, handling undefined values gracefully
  return tableData?.reduce((total, row) => {
    let value;
    if (key === 'netWeight') {
      value = row?.netWeight || row?.summaryData?.netWeight || 0;
    } else if (key === 'netWithWastage') {
      value = row?.netWithWastage || row?.summaryData?.netWithWastage || 0;
    } else if (key === 'pureWeight') {
      value = row?.pureWeight || row?.summaryData?.pureWeight || 0;
    } else {
      value = row?.[key] || 0; // Default handling for other keys
    }
    const cleanedValue =
      typeof value === 'string' ? value.replace(/[^0-9.]/g, '').trim() : value;
    const numericValue = parseFloat(cleanedValue);
    return total + (numericValue || 0); // Ensure numeric summation
  }, 0);
};
export const renderTransactionIssueTableSummary = (row) => {
  const lengthOfItemList = row?.filter(
    (rowItem) => rowItem?.itemId || rowItem?.itemName,
  );
  return (
    <Table.Summary>
      <Table.Summary.Row>
        <Table.Summary.Cell>
          <div className="tw-font-[700] tw-text-[0.77rem] ">Total: </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell>
          <span>
            {lengthOfItemList?.length ? lengthOfItemList?.length : ''}
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>

        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <p className="username">
            {calculateTotals('grossWeight', row).toLocaleString()}
          </p>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <p className="username">
            {calculateTotals('diamondWeight', row).toLocaleString()}
          </p>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <p className="username">
            {calculateTotals('stoneWeight', row).toLocaleString()}
          </p>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <p className="username">
            {calculateTotals('netWeight', row).toLocaleString()}
          </p>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          {/* <p className="username">
            {calculateTotals('wtInTola', row).toLocaleString()}
          </p> */}
        </Table.Summary.Cell>

        <Table.Summary.Cell align="right">
          <p className="username">
            {Number(calculateTotals('pureWeight', row)).toLocaleString()}
          </p>
        </Table.Summary.Cell>

        <Table.Summary.Cell></Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );
};

export const renderTransactionTableSummary = (row, isDL, from) => {
  const lengthOfItemList = row?.filter(
    (rowItem) => rowItem?.itemId || rowItem?.itemName,
  );

  const globalClassName = `tw-font-[700] ${
    isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'
  }`;

  return (
    <Table.Summary>
      <Table.Summary.Row>
        <Table.Summary.Cell>
          <div
            className={`tw-font-[700] ${isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'} `}
          >
            Total:{' '}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell>
          <span>
            {lengthOfItemList?.length ? lengthOfItemList?.length : ''}
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('grossWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('diamondWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('stoneWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('netWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          {/* <div className={globalClassName}>
            {calculateTotals('wtInTola', row).toLocaleString()}
          </div> */}
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {Number(calculateTotals('pureWeight', row)).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {Number(calculateTotals('wastageWeight', row)).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('netWithWastage', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {formatNumberWithComma(calculateTotals('makingAmount', row))}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {formatNumberWithComma(calculateTotals('netAmount', row))}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>
        {from && <Table.Summary.Cell></Table.Summary.Cell>}
      </Table.Summary.Row>
    </Table.Summary>
  );
};

export const renderTransactionTableSummaryPurchase = (row, isDL, from) => {
  const lengthOfItemList = row?.filter(
    (rowItem) => rowItem?.itemId || rowItem?.itemName,
  );

  const globalClassName = `tw-font-[700] ${
    isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'
  }`;

  return (
    <Table.Summary>
      <Table.Summary.Row>
        <Table.Summary.Cell>
          <div
            className={`tw-font-[700] ${isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'} `}
          >
            Total:{' '}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell>
          <span>
            {lengthOfItemList?.length ? lengthOfItemList?.length : ''}
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('grossWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('diamondWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('stoneWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('netWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {Number(calculateTotals('pureWeight', row)).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {Number(calculateTotals('wastageWeight', row)).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('netWithWastage', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {formatNumberWithComma(calculateTotals('makingAmount', row))}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {formatNumberWithComma(calculateTotals('netAmount', row))}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>
        {from && <Table.Summary.Cell></Table.Summary.Cell>}
      </Table.Summary.Row>
    </Table.Summary>
  );
};

export const renderTransactionTableSummaryPurchaseNewLatest = (
  row,
  isDL,
  from,
) => {
  const lengthOfItemList = row?.filter(
    (rowItem) => rowItem?.itemId || rowItem?.itemName,
  );

  const globalClassName = `tw-font-[700] ${
    isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'
  }`;

  const userData = decryptData(localStorage.getItem('userData'));

  const isVat = userData?.isVatApplicable;

  return (
    <Table.Summary>
      <Table.Summary.Row>
        <Table.Summary.Cell>
          <div
            className={`tw-font-[700] ${isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'} `}
          >
            Total:{' '}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell>
          <span>
            {lengthOfItemList?.length ? lengthOfItemList?.length : ''}
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('grossWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('diamondWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('stoneWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('netWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {Number(calculateTotals('pureWeight', row)).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {Number(calculateTotals('wastageWeight', row)).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('netWithWastage', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {formatNumberWithComma(calculateTotals('makingAmount', row))}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {/* {formatNumberWithComma(calculateTotals('netAmount', row))} */}
          </div>
        </Table.Summary.Cell>
        {isVat && <Table.Summary.Cell></Table.Summary.Cell>}
        <Table.Summary.Cell></Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>
        {from && <Table.Summary.Cell></Table.Summary.Cell>}
      </Table.Summary.Row>
    </Table.Summary>
  );
};

export const renderTransactionTableSummaryNew = (row, isDL) => {
  const lengthOfItemList = row?.filter(
    (rowItem) => rowItem?.itemId || rowItem?.itemName,
  );

  const globalClassName = `tw-font-[700] ${
    isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'
  }`;

  return (
    <Table.Summary>
      <Table.Summary.Row>
        <Table.Summary.Cell>
          <div
            className={`tw-font-[700] ${isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'} `}
          >
            Total:{' '}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell>
          <span>
            {lengthOfItemList?.length ? lengthOfItemList?.length : ''}
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('grossWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('diamondWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('stoneWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('netWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {Number(calculateTotals('pureWeight', row)).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {Number(calculateTotals('wastageWeight', row)).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('netWithWastage', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {formatNumberWithComma(calculateTotals('makingAmount', row))}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {formatNumberWithComma(calculateTotals('netAmount', row))}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          {formatNumberWithComma(calculateTotals('discountAmount', row))}
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          {/* {formatNumberWithComma(calculateTotals('netAmountWithTax', row))} */}
        </Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );
};

export const renderTransactionTableSummaryNewReturn = (row, isDL) => {
  const location = useLocation();

  const lengthOfItemList = row?.filter(
    (rowItem) => rowItem?.itemId || rowItem?.itemName,
  );

  const globalClassName = `tw-font-[700] ${
    isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'
  }`;

  const isSalesReturn =
    location.pathname.includes('/sales/return/create/') ||
    location.pathname.includes('/sales/return/edit/');

  return (
    <Table.Summary>
      <Table.Summary.Row>
        <Table.Summary.Cell>
          <div
            className={`tw-font-[700] ${isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'} `}
          >
            Total:{' '}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell>
          <span>
            {lengthOfItemList?.length ? lengthOfItemList?.length : ''}
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('grossWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('diamondWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('stoneWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('netWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {Number(calculateTotals('pureWeight', row)).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {Number(calculateTotals('wastageWeight', row)).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {calculateTotals('netWithWastage', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {formatNumberWithComma(calculateTotals('makingAmount', row))}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className={globalClassName}>
            {formatNumberWithComma(calculateTotals('netAmount', row))}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        {userData?.isTaxApplicable && !isSalesReturn && (
          <Table.Summary.Cell align="right"></Table.Summary.Cell>
        )}
        {userData?.isVatApplicable && !isSalesReturn && (
          <Table.Summary.Cell align="right"></Table.Summary.Cell>
        )}
        <Table.Summary.Cell></Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );
};

export const renderTransactionTableSummarySalesI = (
  row,
  isDL = false,
  isReceiveMetal = false,
  isReceiveMetalSales = false,
) => {
  const lengthOfItemList = row?.filter(
    (rowItem) => rowItem?.itemId || rowItem?.itemName,
  );

  const globalClassName = `tw-font-[700] ${
    isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'
  }`;

  const totalCell = (value) => (
    <Table.Summary.Cell align="right">
      <div className={globalClassName}>{value}</div>
    </Table.Summary.Cell>
  );

  return (
    <Table.Summary>
      <Table.Summary.Row>
        {/* S.N. */}
        <Table.Summary.Cell>
          <div
            className={`tw-font-[700] ${isDL ? 'tw-text-[0.65rem]' : 'tw-text-[0.77rem]'} `}
          >
            Total:{' '}
          </div>
        </Table.Summary.Cell>
        {/* Item */}
        <Table.Summary.Cell>
          <span>
            {lengthOfItemList?.length ? lengthOfItemList?.length : ''}
          </span>
        </Table.Summary.Cell>
        {/* Code */}
        <Table.Summary.Cell></Table.Summary.Cell>
        {/* Purity */}
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        {/* Gwt. */}
        {totalCell(calculateTotals('grossWeight', row).toLocaleString())}
        {/* Dia. Wt. */}
        {totalCell(calculateTotals('diamondWeight', row).toLocaleString())}
        {/* St. Wt. */}
        {totalCell(calculateTotals('stoneWeight', row).toLocaleString())}
        {isReceiveMetalSales ? (
          <>
            {/* P. Wt. */}
            {totalCell(
              Number(calculateTotals('pureWeight', row)).toLocaleString(),
            )}
            {/* Nt. with Wstg */}
            {totalCell(calculateTotals('netWithWastage', row).toLocaleString())}
            {/* Net wt */}
            {totalCell(calculateTotals('netWeight', row).toLocaleString())}
            {/* Pure wt */}
            {totalCell(
              Number(calculateTotals('pureWeight', row)).toLocaleString(),
            )}
            {/* Receive Wt */}
            {totalCell(calculateTotals('receiveWeight', row).toLocaleString())}
            {/* Final Wt */}
            {totalCell(
              calculateTotals('finalWeightToShow', row).toLocaleString(),
            )}
            {/* Wastage Wt */}
            {totalCell(
              Number(calculateTotals('wastageWeight', row)).toLocaleString(),
            )}
            {/* Total Wt */}
            {totalCell(calculateTotals('finalWeight', row).toLocaleString())}
          </>
        ) : (
          <>
            {/* Nt. Wt. */}
            {totalCell(calculateTotals('netWeight', row).toLocaleString())}
            {/* Nt. Wt. (tola/gm) */}
            <Table.Summary.Cell align="right"></Table.Summary.Cell>
            {/* P. Wt. */}
            {totalCell(
              Number(calculateTotals('pureWeight', row)).toLocaleString(),
            )}
            {/* Wstg Wt. */}
            {totalCell(
              Number(calculateTotals('wastageWeight', row)).toLocaleString(),
            )}
            {/* Nt. with Wstg */}
            {totalCell(calculateTotals('netWithWastage', row).toLocaleString())}
            {isReceiveMetal && (
              <>
                {/* Pure with Wastage */}
                {totalCell(
                  calculateTotals('pureWastage', row).toLocaleString(),
                )}
                {/* Receive Wt */}
                {totalCell(
                  calculateTotals('receiveWeight', row).toLocaleString(),
                )}
                {/* Final Wt */}
                {totalCell(
                  calculateTotals('finalWeight', row).toLocaleString(),
                )}
              </>
            )}
          </>
        )}
        {/* Making Amt. */}
        {totalCell(formatNumberWithComma(calculateTotals('makingAmount', row)))}
        {/* Rate */}
        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        {/* Sub Total */}
        {totalCell(formatNumberWithComma(calculateTotals('netAmount', row)))}
        {/* Dis. Amt. */}
        <Table.Summary.Cell align="right">
          {formatNumberWithComma(calculateTotals('discountAmount', row))}
        </Table.Summary.Cell>
        {/* Taxable Amt. */}
        {totalCell(
          formatNumberWithComma(calculateTotals('taxableAmount', row)),
        )}
        {/* Tax Amt. */}
        {userData?.isTaxApplicable &&
          totalCell(formatNumberWithComma(calculateTotals('taxAmount', row)))}
        {/* VAT Amt. */}
        {userData?.isVatApplicable &&
          totalCell(formatNumberWithComma(calculateTotals('vatAmount', row)))}
        {/* Net Amt. */}
        {totalCell(
          formatNumberWithComma(
            calculateTotals('netAmountWithTaxPlusVat', row),
          ),
        )}
        {/* Action */}
        <Table.Summary.Cell></Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );
};

export const renderTransactionTableSummaryQP = (row) => {
  const lengthOfItemList = row?.filter(
    (rowItem) => rowItem?.itemId || rowItem?.itemName,
  );
  return (
    <Table.Summary>
      <Table.Summary.Row>
        <Table.Summary.Cell>
          <div className="tw-font-[700] tw-text-[0.77rem] ">Total: </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell>
          <span>
            {lengthOfItemList?.length ? lengthOfItemList?.length : ''}
          </span>
        </Table.Summary.Cell>
        <Table.Summary.Cell></Table.Summary.Cell>

        <Table.Summary.Cell align="right">
          <div className="tw-font-[600] tw-text-[0.77rem]">
            {calculateTotals('grossWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>

        <Table.Summary.Cell align="right"></Table.Summary.Cell>
        <Table.Summary.Cell align="right"></Table.Summary.Cell>

        <Table.Summary.Cell align="right">
          {calculateTotals('wastageCharge', row).toLocaleString()}
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className="tw-font-[600] tw-text-[0.77rem]">
            {calculateTotals('pureWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell align="right">
          <div className="tw-font-[600] tw-text-[0.77rem]">
            {calculateTotals('fineWeight', row).toLocaleString()}
          </div>
        </Table.Summary.Cell>
        <Table.Summary.Cell>
          <div className="tw-font-[600] tw-text-[0.77rem] tw-text-right">
            {calculateTotals('amount', row)?.toLocaleString()}
          </div>
        </Table.Summary.Cell>

        <Table.Summary.Cell></Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );
};

export const generateNewRowData = (
  form,
  formData,
  newRowData,
  finalData,
  salesSelectedData,
) => {
  return {
    id: newRowData?.id || newRowData?.codeWindowId,
    grossWeight: form?.getFieldValue('grossWeight'),
    purity: form.getFieldValue('purity'),
    pureWeight: form.getFieldValue('pureWeight'),
    diamondWeight: form.getFieldValue('diamondWeightCrt'),
    stoneWeight: form.getFieldValue('stoneWeightGram'),
    netWeight: form.getFieldValue('netWeight'),
    wastageWeight: form.getFieldValue('wastageWeight'),
    makingAmount: form.getFieldValue('makingAmount'),
    rate: salesSelectedData?.isDefault ? 0 : form.getFieldValue('rate'),
    ratePerPurity:
      (salesSelectedData?.isDefault ? 0 : form.getFieldValue('free_rate')) ||
      new Decimal(form.getFieldValue('rate'))
        .mul(new Decimal(form.getFieldValue('purity')))
        .div(100),
    netAmount: form.getFieldValue('netAmount'),
    netWithWastage: form.getFieldValue('netWithWastage'),
    itemName: newRowData?.itemName,
    issueId: newRowData?.issueId,
    itemType: newRowData?.itemType || newRowData?.itemTypeName,
    // code: salesSelectedData?.isDefault
    //   ? null
    //   : newRowData?.code || newRowData?.itemCode,
    code: newRowData?.code || newRowData?.itemCode,
    itemCode: newRowData?.code || newRowData?.itemCode,
    itemBarcodePrefix: newRowData?.itemBarcodePrefix || '',
    finalData: finalData,
    ...(newRowData?.isManualEntry && {
      isManualEntry: newRowData?.isManualEntry,
    }),
    ...(newRowData?.isUseStock && { isUseStock: newRowData?.isUseStock }),
    calculateOnPure: finalData?.calculateOnPure,
  };
};

export const generateNewRowDataDiamondStoneOnly = (newRowData, finalData) => {
  const itemType =
    newRowData?.itemType ||
    newRowData?.itemTypeName ||
    finalData?.detailsJson?.[0]?.itemType;
  const type = itemType?.toLowerCase();
  const firstLine = finalData?.diamondStoneJson?.[0];
  const lineWeight = Number(firstLine?.weight || 0);
  const netAmount = finalData?.detailsJson?.[0]?.netAmount || 0;

  return {
    id: newRowData?.id || newRowData?.codeWindowId,
    grossWeight: 0,
    purity: 0,
    setPcs: 0,
    pureWeight: 0,
    diamondWeight: type === 'diamond' ? lineWeight : 0,
    stoneWeight: type === 'stone' ? lineWeight : 0,
    netWeight: 0,
    wastageWeight: 0,
    makingAmount: 0,
    rate: 0,
    ratePerPurity: 0,
    netAmount,
    subTotal: netAmount,
    netWithWastage: 0,
    item: firstLine?.itemId || newRowData?.itemId || newRowData?.item,
    itemId: firstLine?.itemId || newRowData?.itemId || newRowData?.item,
    itemName: newRowData?.itemName || finalData?.detailsJson?.[0]?.itemName,
    issueId: newRowData?.issueId,
    itemType,
    code: 'NA',
    itemCode: 'NA',
    finalData,
  };
};

export const generateNewRowDataStockAdj = (
  form,
  formData,
  newRowData,
  finalData,
  salesSelectedData,
) => ({
  id: newRowData?.id || newRowData?.codeWindowId,
  grossWeight: form?.getFieldValue('grossWeight'),
  purity: form.getFieldValue('purity'),
  pureWeight: form.getFieldValue('pureWeight'),
  diamondWeight: form.getFieldValue('diamondWeightCrt'),
  stoneWeight: form.getFieldValue('stoneWeightGram'),
  netWeight: form.getFieldValue('netWeight'),
  wastageWeight: form.getFieldValue('wastageWeight'),
  makingAmount: form.getFieldValue('makingAmount'),
  rate: salesSelectedData?.isDefault ? 0 : form.getFieldValue('rate'),
  netAmount: form.getFieldValue('netAmount'),
  netWithWastage: form.getFieldValue('netWithWastage'),
  itemName: newRowData?.itemName,
  issueId: newRowData?.issueId,
  // code: salesSelectedData?.isDefault
  //   ? null
  //   : newRowData?.code || newRowData?.itemCode,
  code: form.getFieldValue('itemCode'),
  finalData: finalData,
});

export const useBarcodeModal = (tableData, columns, navigatePath) => {
  const navigate = useNavigate();
  const [barcodeValue, setBarcodeValue] = useState([]);
  const [extraDetails, setExtraDetails] = useState({});
  const [barCodeVisible, setBarcodeModalVisible] = useState(false);
  const openBarCodeModal = (
    singleValue,
    netWeight,
    grossWeight,
    dWeight,
    sWeight,
    itemName,
    purity,
  ) => {
    setBarcodeModalVisible(true);
    // const barcodeValues = tableData?.length
    //   ? tableData?.map((data) => data?.itemCode) || []
    //   : singleValue;

    const barcodeValues = tableData?.length
      ? tableData.map((data) => data?.itemCode || data?.code).filter(Boolean)
      : singleValue;

    setBarcodeValue(barcodeValues);
    // if (tableData && tableData?.length) {
    //   // If tableData exists and has values, map through the data to set details
    //   // const extraDetails = tableData.map((data) => ({
    //   //   netWeight: data?.summaryData?.netWeight || 0,
    //   //   grossWeight: data?.grossWeight || 0,
    //   //   dWeight: data?.dWeight || 0,
    //   //   sWeight: data?.sWeight || 0,
    //   //   itemName: data?.itemName || 'N/A',
    //   //   netAmount: data?.netAmount || 0,
    //   // }));
    //   let extraDetailsIterate = [];
    //   // tableData.map((table) => {
    //   //   if (table.finalData) {
    //   //     table.finalData.detailsJson.map((detail) => {
    //   //       extraDetailsIterate.push(detail);
    //   //     });
    //   //   } else {
    //   //     extraDetailsIterate.push(table);
    //   //   }
    //   // });
    //   tableData.forEach((table) => {
    //     if (table.finalData) {
    //       table.finalData.detailsJson.forEach((detail) => {
    //         detail.diamondStones = table.finalData.diamondStoneJson;
    //         extraDetailsIterate.push({
    //           ...detail,
    //           itemName: table.itemName, // Append ItemName
    //         });
    //       });
    //     } else {
    //       extraDetailsIterate.push({
    //         ...table,
    //         itemName: table.itemName, // Ensure ItemName is included
    //       });
    //     }
    //   });

    //   setExtraDetails(extraDetailsIterate);
    // } else {
    //   setExtraDetails([]);
    // }
  };

  const closeBarCodeModal = () => {
    setBarcodeModalVisible(false);
    navigate(navigatePath);
  };

  const filterColumnforBillPrint = columns?.filter(
    (column) =>
      column.key !== 'action' &&
      column.title !== 'Action' &&
      column.title !== 'S.No',
  );

  return {
    barcodeValue,
    barCodeVisible,
    openBarCodeModal,
    closeBarCodeModal,
    filterColumnforBillPrint,
    setBarcodeModalVisible,
    extraDetails,
    setExtraDetails,
  };
};

// export const transactionSubmit = async ({
//   formData,
//   jsonName,
//   purchaseTableData,
//   firstjson,
// }) => {
//   const updateCodeWindowJsonResult = updateCodeWindowJson(purchaseTableData);

//   formData.append(jsonName, JSON.stringify(firstjson));

//   updateCodeWindowJsonResult.itemDetails?.forEach((item, index) => {
//     const { image, ...jsonWithoutImage } = item;

//     formData.append(
//       `itemDetails[${index}].data`,
//       JSON.stringify(jsonWithoutImage),
//     );

//     if (image instanceof File) {
//       formData.append(`itemDetails[${index}].image`, image);
//     } else {
//       formData.append(`itemDetails[${index}].image`, 'null');
//     }
//   });
// };

// utils/transactionHelper.js
export const processTransactionData = ({
  selectedData,
  dataSource,
  from,
  salesSelectedData,
  values = {},
  paymentVoucherHeader,
}) => {
  const dateToSend = selectedData?.entryDate
    ? selectedData.entryDate?.format('YYYY-MM-DD')
    : null;
  const deliveryDate = selectedData?.deliveryDate
    ? selectedData?.deliveryDate?.format('YYYY-MM-DD')
    : null;

  const salesOrderJson = {
    billNo: selectedData?.billNo || selectedData?.billNumber,
    orderDate: dateToSend,
    entryNo: selectedData?.entryNumber,
    ledgerId: selectedData?.ledger,
    deliveryDate: deliveryDate,
    agentId: selectedData?.agentId,
  };
  const purchaseJson = {
    purchaseDate: dateToSend || null,
    entryNo: selectedData?.entryNo || selectedData?.entryNumber || null,
    ledgerId: salesSelectedData?.ledger || selectedData?.ledger,
    discountAmount: selectedData?.discountAmount ?? 0,
    remarks: values?.remarks,
    ...(selectedData?.storeId && { storeId: selectedData?.storeId }),
  };
  const receiveJson = {
    billNo: selectedData?.billNumber, // Append top-level properties to each item
    receiveDate: dateToSend,
    entryNo: selectedData?.entryNumber,
    remarks: values?.remarks,
    ledgerId: selectedData?.ledger,
    ...(from === 'refine' && {
      issueBillNumber: selectedData?.issueBillNumber,
    }),
    ...(selectedData?.storeId && { storeId: selectedData?.storeId }),
  };

  const cleanedDataSource = dataSource?.map(({ sno, key, ...rest }) => rest);

  const voucherJson = {
    receiveFrom: selectedData?.ledger,
    billNumber: selectedData?.billNumber,
    voucherDate: selectedData?.entryDate?.format('YYYY-MM-DD'),
    voucherDetails: cleanedDataSource,
  };

  const purchaseVoucherJson = {
    voucherDate: selectedData?.entryDate?.format('YYYY-MM-DD'),
    voucherNo: selectedData?.billNumber,
    voucherType: paymentVoucherHeader?.voucherType,
    ...(paymentVoucherHeader?.voucherId && {
      voucherId: paymentVoucherHeader?.voucherId,
    }),
    narration: paymentVoucherHeader?.remarks || '',
    voucherEntries: [
      ...cleanedDataSource?.map((entry) => ({
        ledger: entry?.payFrom,
        debitAmount: 0,
        creditAmount: Number(entry.amount),
        remarks: entry.remarks ? entry.remarks : null,
      })),
      {
        ledger: selectedData?.ledger,
        creditAmount: 0,
        debitAmount: Number(
          cleanedDataSource?.reduce(
            (acc, val) => acc + parseFloat(val.amount),
            0,
          ),
        ),
        remarks: '',
      },
    ],
  };

  return {
    salesOrderJson,
    purchaseJson,
    receiveJson,
    voucherJson: dataSource
      ? dataSource[0]?.receiveTo !== ''
        ? voucherJson
        : []
      : [],
    purchaseVoucherJson: dataSource
      ? dataSource[0]?.payFrom
        ? purchaseVoucherJson
        : []
      : [],
  };
};

//  updateCodeWindowJsonResult?.itemDetails?.forEach((item, index) => {
//    const { image, fileList, ...jsonWithoutImage } = item;

//    // Append the JSON data for each item
//    formData2.append(
//      `itemDetails[${index}].data`,
//      JSON.stringify(jsonWithoutImage),
//    );

//    // Handle multiple images from fileList
//    if (Array.isArray(fileList) && fileList.length > 0) {
//      fileList.forEach((fileItem, fileIndex) => {
//        const imageToSend = fileItem?.originFileObj || null;
//        if (imageToSend) {
//          formData2.append(
//            `itemDetails[${index}].image[${fileIndex}]`,
//            imageToSend,
//          );
//        }
//      });
//    } else if (image instanceof File) {
//      // If single image exists outside of fileList
//      formData2.append(`itemDetails[${index}].image[0]`, image);
//    } else if (image) {
//      // Fallback for non-File image values (e.g. URL or base64 string)
//      formData2.append(`itemDetails[${index}].image[0]`, image);
//    }
//  });

export const transactionSubmit = ({
  formData,
  jsonName,
  tableData,
  firstJson,
  secondJson,
}) => {
  const updateCodeWindowJsonResult = updateCodeWindowJson(tableData);
  formData.append(jsonName, JSON.stringify(firstJson));
  secondJson && formData.append('voucherJson', JSON.stringify(secondJson));
  updateCodeWindowJsonResult.itemDetails?.forEach((item, index) => {
    const { image, fileList, ...jsonWithoutImage } = item;
    const imageToSend = fileList
      ? fileList[0]?.originFileObj
        ? fileList[0]?.originFileObj
        : null
      : image
        ? image
        : null;

    formData.append(
      `itemDetails[${index}].data`,
      JSON.stringify(jsonWithoutImage),
    );

    if (image instanceof File) {
      formData.append(`itemDetails[${index}].image`, imageToSend);
    } else {
      formData.append(`itemDetails[${index}].image`, imageToSend);
    }
  });

  return updateCodeWindowJsonResult;
};

export const transactionSubmitNewImage = ({
  formData,
  jsonName,
  tableData,
  firstJson,
  secondJson,
}) => {
  const updateCodeWindowJsonResult = updateCodeWindowJson(tableData);

  // Append main JSONs
  formData.append(jsonName, JSON.stringify(firstJson));
  if (secondJson) {
    formData.append('voucherJson', JSON.stringify(secondJson));
  }

  // Process itemDetails
  updateCodeWindowJsonResult.itemDetails?.forEach((item, index) => {
    const { images, fileList, ...jsonWithoutImage } = item;
    // Append stripped JSON (without image/fileList)
    formData.append(
      `itemDetails[${index}].data`,
      JSON.stringify(jsonWithoutImage),
    );

    // Handle multiple images from fileList
    if (Array.isArray(fileList) && fileList.length > 0) {
      fileList.forEach((fileItem, fileIndex) => {
        const imageToSend = fileItem?.originFileObj || null;

        const fileObject = {
          id: fileItem?.id,
          image: fileItem?.url,
        };
        if (imageToSend) {
          formData.append(
            `itemDetails[${index}].images[${fileIndex}]`,
            imageToSend,
          );
        } else {
          formData.append(
            `itemDetails[${index}].images[${fileIndex}]`,
            JSON.stringify(fileObject),
          );
        }
      });
    } else if (images?.length > 0) {
      images?.forEach((image, fileIndex) => {
        // const imageToSend = image?.url || null;
        if (image) {
          formData.append(
            `itemDetails[${index}].images[${fileIndex}]`,
            JSON.stringify(image),
          );
        }
      });
      // Fallback for string/base64/image URL
      // formData.append(`itemDetails[${index}].images[0]`, images);
    }
  });

  return updateCodeWindowJsonResult;
};

export const transactionSubmitPurchase = ({
  formData,
  jsonName,
  tableData,
  firstJson,
  secondJson,
}) => {
  const updateCodeWindowJsonResult = updateCodeWindowJson(tableData);

  // formData.append(jsonName, JSON.stringify(firstJson));
  for (const [key, value] of Object.entries(firstJson)) {
    formData.append(`${key}`, value ?? null);
  }
  secondJson && formData.append('voucherJson', JSON.stringify(secondJson));

  // Prepare itemDetails array with images included
  // const itemDetailsArray = updateCodeWindowJsonResult?.itemDetails?.map(
  //   (item) => {
  //     const { fileList, ...itemWithImage } = item;
  //     if (fileList && fileList[0]?.originFileObj) {
  //       return {
  //         ...itemWithImage,
  //         image: fileList?.[0]?.originFileObj || null,
  //       };
  //     }
  //     return itemWithImage;
  //   },
  // );

  // // Append the entire itemDetails array as a single JSON string
  // formData.append('itemDetails', JSON.stringify(itemDetailsArray || []));

  updateCodeWindowJsonResult?.itemDetails?.forEach((item, index) => {
    const { image, fileList, ...jsonWithoutImage } = item;
    const imageToSend = fileList
      ? fileList[0]?.originFileObj
        ? fileList[0]?.originFileObj
        : null
      : image
        ? image
        : null;

    formData.append(
      `itemDetails[${index}].data`,
      JSON.stringify(jsonWithoutImage),
    );

    if (image instanceof File) {
      formData.append(`itemDetails[${index}].image`, imageToSend);
    } else {
      formData.append(`itemDetails[${index}].image`, imageToSend);
    }
  });

  return updateCodeWindowJsonResult;
};
